/* eslint-disable react/no-unescaped-entities, react/jsx-key */
import { motion } from "framer-motion";
import { MapPin, Phone, Star, Eye, ExternalLink } from "lucide-react";

const benefits = [
  { icon: MapPin, title: "Apparaître sur Google Maps", text: "Vos clients vous trouvent en 1 clic depuis leur mobile." },
  { icon: Phone, title: "Plus d'appels", text: "Bouton « Appeler » directement sur votre fiche." },
  { icon: Star, title: "Plus d'avis clients", text: "Un système qui rassure et fait remonter votre fiche." },
  { icon: Eye, title: "Plus de visibilité locale", text: "Vous prenez la place que vos concurrents vous laissent." },
];

export const GoogleBusiness = () => (
  <section id="google-business" data-testid="gbp-section" className="py-24 md:py-32 bg-white relative overflow-hidden">
    <div className="absolute inset-0 dot-grid opacity-30 -z-0" aria-hidden />
    <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <p className="font-mono-label text-cyan-600 flex items-center">
            <span className="rule bg-cyan" /> Fiche Google
          </p>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl text-ink tracking-tight leading-[1.05]">
            Soyez visible <em className="italic font-light">sur Google Maps</em>.
          </h2>
          <p className="mt-8 text-ink/75 leading-relaxed text-lg">
            Création ou optimisation complète de votre{" "}
            <strong className="text-ink">Page Google (fiche Google Maps)</strong> — le levier le plus
            rentable pour attirer des clients à proximité.
          </p>

          <ul className="mt-10 space-y-5">
            {benefits.map((b, i) => {
              const Icon = b.icon;
              return (
                <motion.li
                  key={b.title}
                  initial={{ opacity: 0, x: -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="flex gap-4"
                  data-testid={`gbp-benefit-${i}`}
                >
                  <span className="h-10 w-10 rounded-full bg-gradient-to-br from-cyan-400 to-cyan-600 inline-flex items-center justify-center text-white shrink-0 cyan-ring">
                    <Icon size={16} />
                  </span>
                  <div>
                    <p className="font-display text-xl text-ink">{b.title}</p>
                    <p className="mt-1 text-sm text-ink/65 leading-relaxed">{b.text}</p>
                  </div>
                </motion.li>
              );
            })}
          </ul>

          <a
            href="#contact"
            data-testid="gbp-cta"
            className="mt-10 inline-flex items-center gap-3 h-14 px-7 btn-premium text-sm font-medium group"
          >
            Optimiser ma fiche Google
            <span className="group-hover:translate-x-0.5 transition-transform">→</span>
          </a>
        </div>

        {/* Mock Google Business card */}
        <div className="lg:col-span-7 lg:pl-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="bg-white border border-ink/15 shadow-[0_30px_80px_-40px_rgba(11,30,61,0.4)] overflow-hidden lift"
          >
            <div className="aspect-[16/9] relative overflow-hidden bg-paper-soft">
              <img
                src="https://images.unsplash.com/photo-1551434678-e076c223a692?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxsb2NhbCUyMGJ1c2luZXNzfGVufDB8fHx8MTc4MTYxNDA0MXww&ixlib=rb-4.1.0&q=85"
                alt="Devanture d'un commerce local dans le Tarn — visibilité Google Maps avec fiche Google Business Profile optimisée par WebUP"
                title="Commerce local optimisé sur Google Business Profile"
                width="960"
                height="540"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 left-4 bg-white px-3 py-1.5 text-xs font-medium shadow flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-cyan animate-pulse" />
                Google Maps — Marssac-sur-Tarn
              </div>
            </div>
            <div className="p-6 md:p-8">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-display text-2xl text-ink">Votre entreprise</p>
                  <p className="text-sm text-ink/55 mt-0.5">Boulangerie · Albi (81)</p>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-0.5 text-amber-500">
                    {[1,2,3,4,5].map((i) => <Star key={i} size={14} fill="currentColor" />)}
                  </div>
                  <p className="text-xs text-ink/55 mt-1">4,9 (128 avis)</p>
                </div>
              </div>
              <div className="mt-5 grid grid-cols-3 gap-2">
                <button className="h-10 bg-gradient-to-br from-cyan-500 to-cyan-600 text-white text-xs font-medium flex items-center justify-center gap-1.5">
                  <Phone size={12} /> Appeler
                </button>
                <button className="h-10 bg-paper-soft border border-ink/15 text-ink text-xs font-medium flex items-center justify-center gap-1.5">
                  <MapPin size={12} /> Itinéraire
                </button>
                <button className="h-10 bg-paper-soft border border-ink/15 text-ink text-xs font-medium flex items-center justify-center gap-1.5">
                  <ExternalLink size={12} /> Site
                </button>
              </div>
              <div className="mt-5 pt-5 border-t border-ink/10 grid grid-cols-3 text-center">
                <div>
                  <p className="font-display text-2xl text-ink">+340 %</p>
                  <p className="text-[10px] text-ink/55 mt-1 uppercase tracking-wider">Vues fiche</p>
                </div>
                <div>
                  <p className="font-display text-2xl text-ink">+120 %</p>
                  <p className="text-[10px] text-ink/55 mt-1 uppercase tracking-wider">Appels</p>
                </div>
                <div>
                  <p className="font-display text-2xl text-ink">+85 %</p>
                  <p className="text-[10px] text-ink/55 mt-1 uppercase tracking-wider">Itinéraires</p>
                </div>
              </div>
              <p className="mt-5 text-[10px] text-ink/45 flex items-center gap-1.5">
                <ExternalLink size={10} /> Source : Google Business Profile Insights — moyenne post-optimisation observée sur nos clients PME du Tarn.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  </section>
);
